/**
 * salsa.ts — Categoría "Salsas y Condimentos" (10 productos demo).
 * Insumos clave para recetas de hamburguesas, hot dogs y platos preparados.
 */
import { buildProduct } from "./_factory";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_salsa",
  skuPrefix: "SAL",
  categoria: "Salsas y Condimentos",
} as const;

export const cat_salsa_products: Producto[] = [
  buildProduct({
    ...CAT,
    index: 0,
    nombre: "Mayonesa Fruco 380g",
    marca: "Fruco",
    modelo: "Tradicional",
    origen: "Colombia",
    costo: 7200,
    precio: 10900,
    stock: 130,
    stockMinimo: 28,
    stockMaximo: 240,
    unidad: "frasco",
    ubicacionFisica: "Estante S1, salsas frías",
    proveedor: "Unilever Andina",
    vencimientoMeses: 12,
    pesoNeto: "380g",
    descripcionLarga:
      "Mayonesa cremosa estilo casero, elaborada con huevos frescos y aceite vegetal. Textura espesa que no se corta al mezclar.\n\nFrasco con boquilla dosificadora.",
    especificaciones: [
      { clave: "Formato", valor: "Frasco 380g" },
      { clave: "Vida útil abierto", valor: "30 días refrigerado" },
    ],
    imageKeywords: ["mayonnaise", "mayonesa", "sauce"],
    color: "bg-amber-50",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 1,
    nombre: "Kétchup Fruco 380g",
    marca: "Fruco",
    modelo: "Tomate Natural",
    origen: "Colombia",
    costo: 6800,
    precio: 10200,
    stock: 140,
    stockMinimo: 30,
    stockMaximo: 250,
    unidad: "frasco",
    ubicacionFisica: "Estante S1, salsas frías",
    proveedor: "Unilever Andina",
    vencimientoMeses: 18,
    pesoNeto: "380g",
    descripcionLarga:
      "Kétchup elaborado con tomates maduros seleccionados. Dulce y ácido balanceado. Acompañante clásico de hamburguesas, papas y hot dogs.",
    especificaciones: [
      { clave: "Tomate", valor: "100% natural" },
      { clave: "Azúcar", valor: "Media" },
    ],
    imageKeywords: ["ketchup", "tomato-sauce", "salsa-tomate"],
    color: "bg-red-600",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 2,
    nombre: "Mostaza Fruco 240g",
    marca: "Fruco",
    modelo: "Tradicional Amarilla",
    origen: "Colombia",
    costo: 5400,
    precio: 8200,
    stock: 110,
    stockMinimo: 25,
    stockMaximo: 200,
    unidad: "frasco",
    ubicacionFisica: "Estante S1, salsas frías",
    proveedor: "Unilever Andina",
    vencimientoMeses: 18,
    pesoNeto: "240g",
    descripcionLarga:
      "Mostaza amarilla suave, aroma tradicional. Realza hot dogs, sándwiches y vinagretas. Sin conservantes artificiales.",
    especificaciones: [
      { clave: "Tipo", valor: "Amarilla suave" },
      { clave: "Picor", valor: "Bajo" },
    ],
    imageKeywords: ["mustard", "mostaza", "yellow-sauce"],
    color: "bg-yellow-500",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 3,
    nombre: "Salsa BBQ Heinz 400g",
    marca: "Heinz",
    modelo: "Original",
    origen: "EE.UU.",
    costo: 9200,
    precio: 14000,
    stock: 85,
    stockMinimo: 18,
    stockMaximo: 160,
    unidad: "frasco",
    ubicacionFisica: "Estante S1, salsas frías",
    proveedor: "Distribuidora Importaciones Gourmet",
    vencimientoMeses: 18,
    pesoNeto: "400g",
    descripcionLarga:
      "Salsa BBQ ahumada, dulce y ligeramente picante. Perfecta para alitas, costillas y hamburguesas estilo americano.",
    especificaciones: [
      { clave: "Perfil", valor: "Ahumada dulce" },
      { clave: "Picor", valor: "Medio" },
    ],
    imageKeywords: ["bbq-sauce", "barbecue", "grill-sauce"],
    color: "bg-red-900",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 4,
    nombre: "Salsa Rosada Fruco 200g",
    marca: "Fruco",
    origen: "Colombia",
    costo: 5800,
    precio: 8700,
    stock: 90,
    stockMinimo: 20,
    stockMaximo: 170,
    unidad: "sobre",
    ubicacionFisica: "Estante S1, salsas frías",
    proveedor: "Unilever Andina",
    vencimientoMeses: 12,
    pesoNeto: "200g",
    descripcionLarga:
      "Salsa rosada cremosa, mezcla equilibrada de mayonesa y kétchup. Acompañante perfecto de hot dogs, papas fritas y mariscos.",
    especificaciones: [{ clave: "Base", valor: "Mayonesa + tomate" }],
    imageKeywords: ["pink-sauce", "salsa-rosada", "condiment"],
    color: "bg-pink-300",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 5,
    nombre: "Salsa de Piña Fruco 200g",
    marca: "Fruco",
    origen: "Colombia",
    costo: 6200,
    precio: 9400,
    stock: 75,
    stockMinimo: 18,
    stockMaximo: 150,
    unidad: "frasco",
    ubicacionFisica: "Estante S1, salsas frías",
    proveedor: "Unilever Andina",
    vencimientoMeses: 12,
    pesoNeto: "200g",
    descripcionLarga:
      "Salsa dulce de piña caramelizada. Ideal para perros calientes colombianos, pollo y cerdo asado. Trozos reales de piña.",
    especificaciones: [{ clave: "Perfil", valor: "Dulce frutal" }],
    imageKeywords: ["pineapple-sauce", "salsa-piña", "sweet-sauce"],
    color: "bg-yellow-400",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 6,
    nombre: "Aceite de Girasol Girasoli 1L",
    marca: "Girasoli",
    modelo: "100% Girasol",
    origen: "Argentina",
    costo: 9800,
    precio: 14800,
    stock: 160,
    stockMinimo: 35,
    stockMaximo: 280,
    unidad: "botella",
    ubicacionFisica: "Estante S2, aceites",
    proveedor: "Team Foods Colombia",
    vencimientoMeses: 24,
    pesoNeto: "1L",
    descripcionLarga:
      "Aceite vegetal 100% girasol, punto de humo alto, ideal para freír, sofreír y hornear. Sin colesterol y liviano al paladar.",
    especificaciones: [
      { clave: "Tipo", valor: "Girasol puro" },
      { clave: "Punto de humo", valor: "232°C" },
    ],
    imageKeywords: ["sunflower-oil", "aceite", "cooking-oil"],
    color: "bg-yellow-300",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 7,
    nombre: "Sal Marina Refisal 1kg",
    marca: "Refisal",
    modelo: "Marina Fina",
    origen: "Colombia",
    costo: 2200,
    precio: 3400,
    stock: 190,
    stockMinimo: 40,
    stockMaximo: 340,
    unidad: "bolsa",
    ubicacionFisica: "Estante S3, especias básicas",
    proveedor: "Brinsa S.A.",
    vencimientoMeses: 36,
    pesoNeto: "1kg",
    descripcionLarga:
      "Sal marina yodada fina, grano parejo de disolución rápida. Indispensable en toda cocina. Empaque resellable.",
    especificaciones: [
      { clave: "Tipo", valor: "Marina fina" },
      { clave: "Yodo", valor: "50 ppm" },
    ],
    imageKeywords: ["salt", "sal", "sea-salt"],
    color: "bg-white",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 8,
    nombre: "Vinagre Blanco Fruco 500ml",
    marca: "Fruco",
    origen: "Colombia",
    costo: 3200,
    precio: 4900,
    stock: 95,
    stockMinimo: 20,
    stockMaximo: 180,
    unidad: "botella",
    ubicacionFisica: "Estante S3, especias básicas",
    proveedor: "Unilever Andina",
    vencimientoMeses: 36,
    pesoNeto: "500ml",
    descripcionLarga:
      "Vinagre blanco destilado 5% acidez. Uso universal: aderezos, encurtidos, limpieza natural y conservación de alimentos.",
    especificaciones: [
      { clave: "Acidez", valor: "5%" },
      { clave: "Origen", valor: "Alcohol destilado" },
    ],
    imageKeywords: ["white-vinegar", "vinagre", "condiment"],
    color: "bg-stone-100",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 9,
    nombre: "Ají Picante Amazon 120g",
    marca: "Amazon",
    modelo: "Picante Tradicional",
    origen: "Colombia",
    costo: 4600,
    precio: 7000,
    stock: 80,
    stockMinimo: 18,
    stockMaximo: 150,
    unidad: "frasco",
    ubicacionFisica: "Estante S1, salsas frías",
    proveedor: "Alimentos Amazon S.A.S.",
    vencimientoMeses: 12,
    pesoNeto: "120g",
    descripcionLarga:
      "Ají colombiano tradicional con cilantro, tomate y cebolla. Fresco, picante justo. Ideal para empanadas, arepas, sopas y asados.",
    especificaciones: [
      { clave: "Picor", valor: "Medio-alto" },
      { clave: "Formato", valor: "Frasco 120g" },
    ],
    imageKeywords: ["hot-sauce", "aji", "chili-sauce"],
    color: "bg-red-700",
    shape: "square",
  }),
];
